import * as proj4 from "proj4"
import { MultiPolygon, Point, Polygon } from "ol/geom"

/**
 * Resolve a place name to an OSM element, then load that element's geometry.
 * Nominatim ranks names and aliases. Overpass then returns the boundary by id,
 * with no project bbox, because a city is usually an administrative relation.
 * Browser requests send the page Referer, which is how Nominatim identifies the app.
 */

const NOMINATIM_SEARCH = "https://nominatim.openstreetmap.org/search"
const NOMINATIM_LOOKUP = "https://nominatim.openstreetmap.org/lookup"

const OVERPASS_ENDPOINTS = [
    "https://overpass-api.de/api/interpreter",
    "https://overpass.kumi.systems/api/interpreter",
    "https://lz4.overpass-api.de/api/interpreter",
]

export interface OverpassCoord {
    lat: number
    lon: number
}

export interface OverpassMember {
    type?: string
    role?: string
    lat?: number
    lon?: number
    geometry?: OverpassCoord[]
}

export interface OverpassElement {
    type?: string
    id?: number
    lat?: number
    lon?: number
    geometry?: OverpassCoord[]
    members?: OverpassMember[]
}

export interface PlaceCandidate {
    osmType: "node" | "way" | "relation"
    osmId: number
    displayName: string
    shortLabel: string
    kind: string
    category: string
    placeType: string
    importance: number
    /** [west, south, east, north] in degrees. For a point this is Nominatim's estimated extent. */
    bbox?: [number, number, number, number]
}

type Ring = Array<[number, number]>

const KIND_LABELS: Record<string, string> = {
    city: "City",
    town: "Town",
    village: "Village",
    hamlet: "Hamlet",
    suburb: "Suburb",
    neighbourhood: "Neighbourhood",
    neighborhood: "Neighbourhood",
    quarter: "Neighbourhood",
    municipality: "Municipality",
    city_district: "District",
    district: "District",
    borough: "District",
    county: "County",
    state: "Region",
    province: "Region",
    region: "Region",
    state_district: "Region",
    country: "Country",
    island: "Island",
    archipelago: "Island",
    continent: "Continent",
    administrative: "Area",
}

const CONTEXT_KEYS = ["municipality", "county", "state", "province", "region", "country"]

export function friendlyPlaceKind(placeType: string, category: string, addressType = ""): string {
    const key = (addressType || placeType || category || "").trim().toLowerCase()
    if (KIND_LABELS[key]) return KIND_LABELS[key]
    if (!key) return "Place"
    return key.replace(/_/g, " ").replace(/\b\w/g, letter => letter.toUpperCase())
}

function addressFields(raw: unknown): Record<string, string> {
    if (!raw || typeof raw !== "object") return {}
    const fields: Record<string, string> = {}
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof value === "string" && value.trim()) fields[key] = value.trim()
    }
    return fields
}

/** "Cambridge, Cambridgeshire, England, United Kingdom" rather than the raw Nominatim string. */
export function placeContextLabel(name: string, address: Record<string, string>, addressType: string): string {
    const placeName = name.trim()
    const parts: string[] = []
    const seen = new Set<string>()
    const add = (value: string) => {
        const trimmed = value.trim()
        const key = trimmed.toLowerCase()
        if (!trimmed || seen.has(key)) return
        seen.add(key)
        parts.push(trimmed)
    }
    add(placeName)
    for (const key of CONTEXT_KEYS) {
        if (key === addressType) continue
        add(address[key] ?? "")
    }
    return parts.join(", ")
}

export function placeCandidateLabel(candidate: PlaceCandidate): { name: string, badge: string } {
    const badge = candidate.kind || friendlyPlaceKind(candidate.placeType, candidate.category)
    const name = candidate.shortLabel || candidate.displayName
    return {
        badge,
        name: name.length > 140 ? `${name.slice(0, 137)}...` : name,
    }
}

export function readPlaceCandidates(raw: unknown): PlaceCandidate[] {
    if (!Array.isArray(raw)) return []
    const candidates: PlaceCandidate[] = []
    for (const item of raw) {
        if (!item || typeof item !== "object") continue
        const rec = item as Partial<PlaceCandidate>
        if (rec.osmType !== "node" && rec.osmType !== "way" && rec.osmType !== "relation") continue
        if (typeof rec.osmId !== "number" || !Number.isFinite(rec.osmId)) continue
        candidates.push({
            osmType: rec.osmType,
            osmId: rec.osmId,
            displayName: typeof rec.displayName === "string" ? rec.displayName : `${rec.osmType}/${rec.osmId}`,
            shortLabel: typeof rec.shortLabel === "string" && rec.shortLabel.trim()
                ? rec.shortLabel
                : (typeof rec.displayName === "string" ? rec.displayName : `${rec.osmType}/${rec.osmId}`),
            kind: typeof rec.kind === "string" && rec.kind.trim()
                ? rec.kind
                : friendlyPlaceKind(
                    typeof rec.placeType === "string" ? rec.placeType : "",
                    typeof rec.category === "string" ? rec.category : ""
                ),
            category: typeof rec.category === "string" ? rec.category : "",
            placeType: typeof rec.placeType === "string" ? rec.placeType : "",
            importance: typeof rec.importance === "number" && Number.isFinite(rec.importance) ? rec.importance : 0,
            bbox: storedBoundingBox(rec.bbox),
        })
    }
    return candidates
}

function placeScore(candidate: PlaceCandidate): number {
    let score = candidate.importance
    if (candidate.osmType === "relation") score += 2
    else if (candidate.osmType === "way") score += 1
    else score -= 2
    if (candidate.category === "boundary" || candidate.placeType === "administrative") score += 1
    if (candidate.category === "place") score += 0.25
    return score
}

export function parseNominatimPlaces(payload: unknown): PlaceCandidate[] {
    if (!Array.isArray(payload)) return []
    const seen = new Set<string>()
    const parsed: PlaceCandidate[] = []
    for (const item of payload) {
        if (!item || typeof item !== "object") continue
        const rec = item as Record<string, unknown>
        const osmType = rec.osm_type
        const osmId = Number(rec.osm_id)
        if (osmType !== "node" && osmType !== "way" && osmType !== "relation") continue
        if (!Number.isInteger(osmId) || osmId <= 0) continue
        const key = `${osmType}/${osmId}`
        if (seen.has(key)) continue
        seen.add(key)
        const importance = Number(rec.importance)
        const addressType = String(rec.addresstype ?? "")
        const placeName = String(rec.name ?? "").trim()
        const displayName = String(rec.display_name || placeName || key)
        const address = addressFields(rec.address)
        const category = String(rec.category ?? rec.class ?? "")
        const placeType = String(rec.type ?? "")
        const shortLabel = placeContextLabel(placeName || displayName.split(",")[0] || displayName, address, addressType)
        parsed.push({
            osmType,
            osmId,
            displayName,
            shortLabel: shortLabel || displayName,
            kind: friendlyPlaceKind(placeType, category, addressType),
            category,
            placeType,
            importance: Number.isFinite(importance) ? importance : 0,
            bbox: boundingBox(rec.boundingbox),
        })
    }

    const pool = preferPlaceAreas(parsed)
    pool.sort((a, b) => placeScore(b) - placeScore(a))
    return pool.slice(0, 5)
}

/** A boundary or place area, not a road or river that happens to share the name. */
function isPlaceArea(candidate: PlaceCandidate): boolean {
    if (candidate.osmType === "node") return false
    return candidate.category === "boundary"
        || candidate.category === "place"
        || candidate.placeType === "administrative"
}

function isPlaceNode(candidate: PlaceCandidate): boolean {
    return candidate.osmType === "node" && (candidate.category === "place" || candidate.category === "boundary")
}

/**
 * Keep a town or village point when the other hits are lines.
 * A real boundary still wins, so Jakarta stays the administrative area.
 */
function preferPlaceAreas(candidates: PlaceCandidate[]): PlaceCandidate[] {
    const areas = candidates.filter(isPlaceArea)
    if (areas.length > 0) return areas
    const nodes = candidates.filter(isPlaceNode)
    if (nodes.length > 0) return nodes
    const features = candidates.filter(candidate => candidate.osmType !== "node")
    return features.length > 0 ? features : candidates
}

function storedBoundingBox(raw: unknown): [number, number, number, number] | undefined {
    if (!Array.isArray(raw) || raw.length < 4) return undefined
    const west = Number(raw[0])
    const south = Number(raw[1])
    const east = Number(raw[2])
    const north = Number(raw[3])
    if (![west, south, east, north].every(value => Number.isFinite(value))) return undefined
    if (!(north > south) || !(east > west)) return undefined
    return [west, south, east, north]
}

function boundingBox(raw: unknown): [number, number, number, number] | undefined {
    if (!Array.isArray(raw) || raw.length < 4) return undefined
    const south = Number(raw[0])
    const north = Number(raw[1])
    const west = Number(raw[2])
    const east = Number(raw[3])
    if (![south, north, west, east].every(value => Number.isFinite(value))) return undefined
    if (!(north > south) || !(east > west)) return undefined
    return [west, south, east, north]
}

/** Nominatim's rank-based box around a place that has no outline. */
export function expandPointGeometry(
    geometry: Point | Polygon | MultiPolygon | null,
    bbox: [number, number, number, number] | undefined
): Point | Polygon | MultiPolygon | null {
    if (!(geometry instanceof Point) || !bbox) return geometry
    const [west, south, east, north] = bbox
    return new Polygon([projectRing([
        [west, south],
        [east, south],
        [east, north],
        [west, north],
        [west, south],
    ])])
}

export function estimatedExtentKm(bbox: [number, number, number, number]): number {
    const [west, south, east, north] = bbox
    const midLat = ((south + north) / 2) * Math.PI / 180
    const width = (east - west) * 111.32 * Math.cos(midLat)
    const height = (north - south) * 111.32
    return Math.max(width, height)
}

export function choosePlace(candidates: PlaceCandidate[], selectedId: unknown): PlaceCandidate | null {
    if (candidates.length === 0) return null
    const id = typeof selectedId === "string" || typeof selectedId === "number" ? Number(selectedId) : NaN
    if (Number.isInteger(id)) {
        const match = candidates.find(candidate => candidate.osmId === id)
        if (match) return match
    }
    return candidates[0]
}

async function fetchJson(url: string, timeoutMs: number): Promise<unknown> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    try {
        const response = await fetch(url, {
            signal: controller.signal,
            headers: {
                Accept: "application/json",
                "Accept-Language": "en",
            },
        })
        if (!response.ok) {
            throw new Error(`Nominatim error: ${response.status} ${response.statusText}`)
        }
        const payload = await response.json()
        if (payload && typeof payload === "object" && !Array.isArray(payload) && "error" in payload) {
            throw new Error(`Nominatim error: ${String((payload as { error: unknown }).error)}`)
        }
        return payload
    } finally {
        clearTimeout(timeoutId)
    }
}

export async function searchPlaces(query: string): Promise<PlaceCandidate[]> {
    const url = `${NOMINATIM_SEARCH}?format=jsonv2&addressdetails=1&limit=8&q=${encodeURIComponent(query)}`
    const payload = await fetchJson(url, 20000)
    return parseNominatimPlaces(payload)
}

function nominatimObjectId(candidate: PlaceCandidate): string {
    const prefix = candidate.osmType === "relation" ? "R" : candidate.osmType === "way" ? "W" : "N"
    return `${prefix}${candidate.osmId}`
}

/**
 * Nominatim can return the boundary already thinned to `thresholdDegrees`.
 * That skips the full-resolution Overpass outline, which is what makes a
 * city or country slow to load.
 */
export async function fetchPlaceBoundary(
    candidate: PlaceCandidate,
    thresholdDegrees: number
): Promise<Point | Polygon | MultiPolygon | null> {
    const threshold = Math.max(thresholdDegrees, 0.00001)
    const url = `${NOMINATIM_LOOKUP}?format=jsonv2&polygon_geojson=1&polygon_threshold=${threshold.toFixed(5)}&osm_ids=${nominatimObjectId(candidate)}`
    const payload = await fetchJson(url, 30000)
    const row = Array.isArray(payload) ? payload[0] : null
    if (!row || typeof row !== "object") return null
    const geojson = (row as { geojson?: unknown }).geojson
    return geometryFromGeoJSON(geojson)
}

export function overpassElementQuery(candidate: PlaceCandidate): string {
    if (!Number.isInteger(candidate.osmId) || candidate.osmId <= 0) {
        throw new Error(`Invalid OSM id: ${candidate.osmId}`)
    }
    if (candidate.osmType !== "node" && candidate.osmType !== "way" && candidate.osmType !== "relation") {
        throw new Error(`Invalid OSM type: ${candidate.osmType}`)
    }
    return `[out:json][timeout:90][maxsize:536870912];\n${candidate.osmType}(${candidate.osmId});\nout geom;`
}

export async function fetchPlaceElement(candidate: PlaceCandidate): Promise<OverpassElement | null> {
    const query = overpassElementQuery(candidate)
    const json = await fetchOverpass(query)
    const elements = Array.isArray(json?.elements) ? json.elements as OverpassElement[] : []
    return elements.find(element => element.type === candidate.osmType && element.id === candidate.osmId)
        ?? elements.find(element => element.type === candidate.osmType)
        ?? elements[0]
        ?? null
}

async function fetchOverpass(query: string): Promise<{ elements?: OverpassElement[], error?: unknown }> {
    let lastError: Error | null = null

    for (const endpoint of OVERPASS_ENDPOINTS) {
        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                if (attempt > 0) {
                    const delay = Math.min(1000 * Math.pow(2, attempt), 10000)
                    await new Promise(resolve => setTimeout(resolve, delay))
                }

                const controller = new AbortController()
                const timeoutId = setTimeout(() => controller.abort(), 100000)
                const response = await fetch(`${endpoint}?data=${encodeURIComponent(query)}`, {
                    signal: controller.signal,
                })
                clearTimeout(timeoutId)

                if (!response.ok) {
                    if (response.status >= 500 && attempt < 2) continue
                    throw new Error(`Overpass API error: ${response.status} ${response.statusText}`)
                }

                const json = await response.json()
                if (json?.error) {
                    const errorMsg = String(json.error).toLowerCase()
                    if ((errorMsg.includes("timeout") || errorMsg.includes("rate limit")) && attempt < 2) {
                        await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
                        continue
                    }
                    throw new Error(`Overpass API error: ${json.error}`)
                }
                return json
            } catch (error: any) {
                lastError = error
                if (error?.name === "AbortError") {
                    if (attempt < 2) continue
                    break
                }
                const message = String(error?.message ?? "")
                if (message.includes("Failed to fetch") || message.includes("NetworkError")) break
                if (attempt < 2) continue
            }
        }
    }

    throw lastError || new Error("Failed to retrieve data from all Overpass API endpoints")
}

function coordKey(lon: number, lat: number): string {
    return `${lon.toFixed(6)},${lat.toFixed(6)}`
}

function samePoint(a: [number, number], b: [number, number]): boolean {
    return coordKey(a[0], a[1]) === coordKey(b[0], b[1])
}

function isClosed(ring: Ring): boolean {
    return ring.length >= 4 && samePoint(ring[0], ring[ring.length - 1])
}

function dedupeConsecutive(ring: Ring): Ring {
    const out: Ring = []
    for (const point of ring) {
        const prev = out[out.length - 1]
        if (!prev || !samePoint(prev, point)) out.push(point)
    }
    return out
}

interface OpenWay {
    coords: Ring
}

function endpointKey(point: [number, number]): string {
    return coordKey(point[0], point[1])
}

function pushWay(index: Map<string, OpenWay[]>, key: string, way: OpenWay) {
    const list = index.get(key)
    if (list) list.push(way)
    else index.set(key, [way])
}

function pullWay(index: Map<string, OpenWay[]>, key: string, way: OpenWay) {
    const list = index.get(key)
    if (!list) return
    const at = list.indexOf(way)
    if (at >= 0) list.splice(at, 1)
    if (list.length === 0) index.delete(key)
}

function takeWay(index: Map<string, OpenWay[]>, key: string, open: Set<OpenWay>): OpenWay | null {
    const list = index.get(key)
    if (!list) return null
    for (let i = list.length - 1; i >= 0; i--) {
        if (open.has(list[i])) return list[i]
    }
    return null
}

function joinWays(current: Ring, next: Ring, mode: "append" | "appendRev" | "prepend" | "prependRev"): Ring {
    if (mode === "append") return current.concat(next.slice(1))
    if (mode === "appendRev") return current.concat(next.slice(0, -1).reverse())
    if (mode === "prepend") return next.concat(current.slice(1))
    return next.slice().reverse().concat(current.slice(1))
}

/** Join boundary ways that share endpoints into closed rings. */
export function stitchWays(ways: Ring[]): Ring[] {
    const rings: Ring[] = []
    const open = new Set<OpenWay>()
    const byStart = new Map<string, OpenWay[]>()
    const byEnd = new Map<string, OpenWay[]>()

    const remember = (way: OpenWay) => {
        pushWay(byStart, endpointKey(way.coords[0]), way)
        pushWay(byEnd, endpointKey(way.coords[way.coords.length - 1]), way)
        open.add(way)
    }
    const forget = (way: OpenWay) => {
        pullWay(byStart, endpointKey(way.coords[0]), way)
        pullWay(byEnd, endpointKey(way.coords[way.coords.length - 1]), way)
        open.delete(way)
    }

    for (const way of ways) {
        const coords = dedupeConsecutive(way)
        if (coords.length < 2) continue
        if (isClosed(coords)) rings.push(coords)
        else remember({ coords })
    }

    while (open.size > 0) {
        let current = open.values().next().value as OpenWay
        forget(current)
        let guard = ways.length + 1
        while (!isClosed(current.coords) && guard-- > 0) {
            const startKey = endpointKey(current.coords[0])
            const endKey = endpointKey(current.coords[current.coords.length - 1])
            let next = takeWay(byStart, endKey, open)
            let mode: "append" | "appendRev" | "prepend" | "prependRev" = "append"
            if (!next) {
                next = takeWay(byEnd, endKey, open)
                mode = "appendRev"
            }
            if (!next) {
                next = takeWay(byEnd, startKey, open)
                mode = "prepend"
            }
            if (!next) {
                next = takeWay(byStart, startKey, open)
                mode = "prependRev"
            }
            if (!next) break
            forget(next)
            current = { coords: joinWays(current.coords, next.coords, mode) }
        }
        if (isClosed(current.coords)) rings.push(current.coords)
    }
    return rings
}

function coordsFromGeometry(geometry: OverpassCoord[] | undefined): Ring | null {
    if (!geometry || geometry.length < 2) return null
    const ring: Ring = []
    for (const point of geometry) {
        if (typeof point?.lon !== "number" || typeof point?.lat !== "number") continue
        if (!Number.isFinite(point.lon) || !Number.isFinite(point.lat)) continue
        ring.push([point.lon, point.lat])
    }
    return ring.length >= 2 ? ring : null
}

export function boundaryRings(element: OverpassElement): { outers: Ring[], inners: Ring[] } {
    if (element.type === "way" || (!element.members && element.geometry)) {
        const ring = coordsFromGeometry(element.geometry)
        if (!ring) return { outers: [], inners: [] }
        return { outers: stitchWays([ring]), inners: [] }
    }

    const outerWays: Ring[] = []
    const innerWays: Ring[] = []
    for (const member of element.members ?? []) {
        if (member.type !== "way") continue
        const ring = coordsFromGeometry(member.geometry)
        if (!ring) continue
        const role = (member.role ?? "").trim().toLowerCase()
        if (role === "inner") innerWays.push(ring)
        else outerWays.push(ring)
    }
    return {
        outers: stitchWays(outerWays),
        inners: stitchWays(innerWays),
    }
}

function ringContains(ring: Ring, point: [number, number]): boolean {
    const [x, y] = point
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i]
        const [xj, yj] = ring[j]
        const crosses = (yi > y) !== (yj > y)
            && x < ((xj - xi) * (y - yi)) / ((yj - yi) || Number.EPSILON) + xi
        if (crosses) inside = !inside
    }
    return inside
}

function ringArea(ring: Ring): number {
    let sum = 0
    for (let i = 0; i < ring.length - 1; i++) {
        sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1]
    }
    return Math.abs(sum) / 2
}

function project(lon: number, lat: number): [number, number] {
    const converter = (proj4 as any).default ?? proj4
    const projected = converter("EPSG:4326", "EPSG:3857", [lon, lat]) as [number, number]
    return [projected[0], projected[1]]
}

function projectRing(ring: Ring): Ring {
    return ring.map(([lon, lat]) => project(lon, lat))
}

function isLonLat(value: unknown): value is [number, number] {
    return Array.isArray(value)
        && value.length >= 2
        && typeof value[0] === "number"
        && typeof value[1] === "number"
        && Number.isFinite(value[0])
        && Number.isFinite(value[1])
}

function projectedRing(raw: unknown): Ring | null {
    if (!Array.isArray(raw)) return null
    const ring: Ring = []
    for (const position of raw) {
        if (!isLonLat(position)) continue
        ring.push(project(position[0], position[1]))
    }
    if (ring.length < 4) return null
    if (!samePoint(ring[0], ring[ring.length - 1])) ring.push(ring[0])
    return ring
}

export function geometryFromGeoJSON(geojson: unknown): Point | Polygon | MultiPolygon | null {
    if (!geojson || typeof geojson !== "object") return null
    const geometry = geojson as { type?: string, coordinates?: unknown }
    if (geometry.type === "Point" && isLonLat(geometry.coordinates)) {
        return new Point(project(geometry.coordinates[0], geometry.coordinates[1]))
    }
    if (geometry.type === "Polygon" && Array.isArray(geometry.coordinates)) {
        const rings = geometry.coordinates
            .map(projectedRing)
            .filter((ring): ring is Ring => ring !== null)
        return rings.length > 0 ? new Polygon(rings) : null
    }
    if (geometry.type === "MultiPolygon" && Array.isArray(geometry.coordinates)) {
        const polygons: Ring[][] = []
        for (const polygon of geometry.coordinates) {
            if (!Array.isArray(polygon)) continue
            const rings = polygon.map(projectedRing).filter((ring): ring is Ring => ring !== null)
            if (rings.length > 0) polygons.push(rings)
        }
        if (polygons.length === 0) return null
        if (polygons.length === 1) return new Polygon(polygons[0])
        return new MultiPolygon(polygons)
    }
    return null
}

export function geometryFromOverpassElement(element: OverpassElement): Point | Polygon | MultiPolygon | null {
    if (element.type === "node") {
        if (typeof element.lon !== "number" || typeof element.lat !== "number") return null
        if (!Number.isFinite(element.lon) || !Number.isFinite(element.lat)) return null
        return new Point(project(element.lon, element.lat))
    }

    const { outers, inners } = boundaryRings(element)
    if (outers.length === 0) return null

    const polygons = outers.map(outer => ({
        outer,
        holes: [] as Ring[],
        area: ringArea(outer),
    }))
    for (const inner of inners) {
        const probe = inner[Math.min(1, inner.length - 1)]
        let best: (typeof polygons)[number] | null = null
        for (const polygon of polygons) {
            if (ringContains(polygon.outer, probe) && (!best || polygon.area < best.area)) {
                best = polygon
            }
        }
        if (best) best.holes.push(inner)
    }

    const built = polygons.map(polygon => new Polygon([
        projectRing(polygon.outer),
        ...polygon.holes.map(projectRing),
    ]))
    if (built.length === 1) return built[0]
    return new MultiPolygon(built.map(polygon => polygon.getCoordinates()))
}
